import { IOnboardingBankRequest } from '@/@types/Onboarding';
import { toast } from '@/hooks/use-toast';
import { OnboardingService } from '@/services/onboarding.service';
import { ProfileService } from '@/services/profile.service';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { cnpj, cpf } from 'cpf-cnpj-validator';
import { useAuth } from '@/auth/Auth';
import { isChallengeTokenError, formatApiFieldError } from '@/hooks/useSecurityChallenge';

const CreateBankSchema = (t: (key: string) => string) =>
  z
    .object({
      bank_code: z
        .string()
        .min(1, t('required_field'))
        .max(4, t('bank_code_max_length')),
      account_digit: z
        .string()
        .min(1, t('required_field'))
        .regex(/^\d{1}$/, t('required_field')),
      account_number: z
        .string()
        .min(1, t('required_field'))
        .regex(/^\d+$/, t('account_number_digits')),
      agency_number: z
        .string()
        .min(1, t('required_field'))
        .regex(/^\d+$/, t('agency_number_digits')),
      agency_digit: z
        .string()
        .min(1, t('required_field'))
        .regex(/^[0-9X]$/, t('agency_digit_invalid')),
      pix_key_type: z.string().min(1, t('pix_key_required')),
      pix_key: z
        .string()
        .min(1, t('required_field'))
        .max(255, t('pix_key_max_length')),
      account_type: z.string().min(1, t('required_field')),
      phone_prefix: z.string().optional(),
      currency: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const pixKeyType = String(data.pix_key_type || '').trim();
      const pixKey = String(data.pix_key || '').trim();

      if (!pixKeyType || !pixKey) return;

      if (pixKeyType === 'individual') {
        const digits = pixKey.replace(/\D/g, '');
        if (!cpf.isValid(digits)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pix_key'],
            message: t('common:invalid_document'),
          });
        }
      }

      if (pixKeyType === 'business') {
        const digits = pixKey.replace(/\D/g, '');
        if (!cnpj.isValid(digits)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pix_key'],
            message: t('common:invalid_document'),
          });
        }
      }

      if (pixKeyType === 'email') {
        const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pixKey);
        if (!looksLikeEmail) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pix_key'],
            message: t('validation:invalid_email'),
          });
        }
      }

      if (pixKeyType === 'phone') {
        const digits = pixKey.replace(/\D/g, '');
        const prefix = String(data.phone_prefix || '').replace(/\D/g, '');

        if (!prefix) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['phone_prefix'],
            message: t('required_field'),
          });
        }

        // Reaproveita mensagem já existente (mesma regra de tamanho usada no projeto)
        if (digits.length < 10 || digits.length > 15) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['pix_key'],
            message: t('validationInvalidWhatsApp'),
          });
        }
      }
    });

interface UseOnboardingBankStepProps {
  onClose: () => void;
  mode: 'create' | 'edit';
  isOnboarding?: boolean;
  onChallengeTokenError?: () => void;
}

export const useOnboardingBankStep = ({
  onClose,
  mode,
  isOnboarding = false,
  onChallengeTokenError,
}: UseOnboardingBankStepProps) => {
  const { t } = useTranslation(['common', 'validation', 'onboarding']);
  const queryClient = useQueryClient();
  const { authData } = useAuth();

  const { data: bank, isLoading } = useQuery({
    queryKey: ['list-banks', authData?.id],
    queryFn: async () => {
      const response = await OnboardingService.getBanks();
      return response?.data?.[0];
    },
    enabled: mode === 'edit' && Boolean(authData?.id),
  });

  const emptyBankValues: IOnboardingBankRequest = {
    bank_code: '',
    account_digit: '',
    account_number: '',
    agency_number: '',
    agency_digit: '',
    pix_key_type: '',
    pix_key: '',
    account_type: '',
    phone_prefix: '',
    currency: 'BRL',
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    setError,
    getValues,
    watch,
    reset,
    clearErrors,
  } = useForm<IOnboardingBankRequest>({
    resolver: zodResolver(CreateBankSchema(t)),
    defaultValues: {
      bank_code: bank?.bank_code || '',
      account_digit: bank?.account_digit || '',
      account_number: bank?.account_number || '',
      agency_number: bank?.agency_number || '',
      agency_digit: bank?.agency_digit || '',
      pix_key_type: bank?.pix_key_type || '',
      pix_key: bank?.pix_key || '',
      account_type: bank?.account_type || '',
      phone_prefix: bank?.phone_prefix || '',
      currency: bank?.currency || 'BRL',
    },
  });

  const previousUserIdRef = useRef<string | number | undefined>(undefined);

  useEffect(() => {
    const userId = authData?.id;
    if (previousUserIdRef.current === userId) return;
    previousUserIdRef.current = userId;
    reset(emptyBankValues);
  }, [authData?.id, reset]);

  useEffect(() => {
    if (mode !== 'edit' || isLoading) return;

    if (bank) {
      reset({
        bank_code: bank.bank_code,
        account_digit: bank.account_digit,
        account_number: bank.account_number,
        agency_number: bank.agency_number,
        agency_digit: bank.agency_digit,
        pix_key_type: bank.pix_key_type,
        pix_key: bank.pix_key,
        account_type: bank.account_type,
        phone_prefix: bank.phone_prefix || '',
        currency: bank.currency || 'BRL',
      });
      return;
    }

    reset(emptyBankValues);
  }, [bank, mode, reset, isLoading, authData?.id]);

  const defaultValues = watch();
  useEffect(() => {
    if (defaultValues.pix_key_type) {
      clearErrors('pix_key_type');
    }
    if (defaultValues?.bank_code) {
      clearErrors('bank_code');
    }
    if (defaultValues?.agency_digit) {
      clearErrors('agency_digit');
    }
  }, [
    defaultValues?.pix_key_type,
    defaultValues?.bank_code,
    defaultValues?.agency_digit,
    clearErrors,
  ]);

  const mutationFn = ({
    data,
    challengeToken,
  }: {
    data: IOnboardingBankRequest;
    challengeToken?: string;
  }) => {
    if (isOnboarding) {
      return OnboardingService.createBankAccount(data);
    }
    return ProfileService.updateBankAccount(data, challengeToken);
  };

  const { mutate, isPending: isSubmitting } = useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user-info'] });
      await queryClient.invalidateQueries({ queryKey: ['list-banks'] });
      await queryClient.invalidateQueries({ queryKey: ['my-account', 'brl-banks'] });
      toast({ description: t('common:data_saved'), variant: 'success' });
      onClose();
    },
    onError: (err: any) => {
      if (isChallengeTokenError(err)) {
        onChallengeTokenError?.();
        return;
      }
      if (err?.response?.data?.errors) {
        Object.entries(err.response.data.errors).forEach(([key, message]) => {
          if (key === 'challenge_token') return;
          setError(key as keyof IOnboardingBankRequest, {
            type: 'manual',
            message: formatApiFieldError(message as string | string[]),
          });
        });
      }
    },
  });

  const onSubmit = (data: IOnboardingBankRequest, challengeToken?: string) => {
    if (!isOnboarding && !challengeToken) {
      onChallengeTokenError?.();
      return;
    }
    mutate({ data, challengeToken });
  };

  return {
    onSubmit,
    handleSubmit,
    register,
    errors,
    isDirty,
    clearErrors,
    getValues,
    setValue,
    watch,
    isSubmitting,
    defaultValues,
    reset,
    isLoading,
  };
};
